import { downloadBlobFromApi, formatPhone } from '../../helpers'
import { IJobApplicationFields } from '../../pages/Dashboard/JobApplications/JobApplicationFormModal'
import apiService from '../Api'
import {
  JobApplication,
  JobApplicationComplete,
  JobApplicationPositions,
  JobApplicationRequisitionOption,
  JobApplicationStatuses
} from './types'

export class JobApplicationService {
  async list(queryParams: {
    search: string
    length: number
    page: number
    status: string
    position: string
    area: string
    salary_range_id?: string
    requisitionId: number | null
  }): Promise<{ data: JobApplication[]; recordsFiltered: number; recordsTotal: number }> {
    const params = new URLSearchParams()
    params.append('search', queryParams.search)
    params.append('length', queryParams.length.toString())
    params.append('start', ((queryParams.page - 1) * queryParams.length).toString())
    params.append('status', queryParams.status)
    params.append('job_position', queryParams.position)
    params.append('area', queryParams.area)
    if (queryParams.salary_range_id) {
      params.append('salary_range_id', queryParams.salary_range_id)
    }
    if (queryParams.requisitionId) {
      params.append('job_requisition_id', String(queryParams.requisitionId))
    }

    return apiService.get('/job-application', { params }).then((response) => {
      const data = response.data.data.map((item: JobApplication) => ({ ...item, phone: formatPhone(item.phone) }))

      return { ...response.data, data }
    })
  }

  async positions(): Promise<JobApplicationPositions> {
    return apiService.get('/job-application/positions').then((response) => response.data)
  }

  /** RPs that went live (open, completed or cancelled), newest first. */
  async requisitions(): Promise<JobApplicationRequisitionOption[]> {
    return apiService.get('/job-application/requisitions').then((response) => response.data)
  }

  async get(id: number): Promise<JobApplicationComplete> {
    return apiService.get(`/job-application/${id}`).then((response) => {
      return { ...response.data, phone: formatPhone(response.data.phone) }
    })
  }

  async delete(id: number): Promise<void> {
    return apiService.delete(`/job-application/${id}`)
  }

  async updateStatus(id: number, newStatus: JobApplicationStatuses): Promise<void> {
    return apiService.post(`/job-application/change-status/${id}/${newStatus}`)
  }

  async create(form: IJobApplicationFields): Promise<void> {
    const formData = new FormData()
    formData.append('name', form.name)
    formData.append('email', form.email)
    formData.append('phone', form.phone)
    if (form.job_requisition_id) {
      formData.append('job_requisition_id', String(form.job_requisition_id))
    }
    formData.append('status', form.status)
    form.area.forEach((area) => {
      formData.append('area[]', area)
    })
    formData.append('why_you_want_to_work_here', form.why_you_want_to_work_here)
    formData.append('reasons_to_hire_you', form.reasons_to_hire_you)
    if (form.resume) {
      formData.append('resume', form.resume)
    }
    if (form.salary_range_id) {
      formData.append('salary_range_id', form.salary_range_id)
    }
    if (form.portfolio_link) {
      formData.append('portfolio_link', form.portfolio_link)
    }

    return apiService.post('/job-application', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async update(id: number, form: IJobApplicationFields): Promise<void> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', form.name)
    formData.append('email', form.email)
    formData.append('phone', form.phone)
    formData.append('status', form.status)
    // An application from before the RPs may stay without one.
    if (form.job_requisition_id) {
      formData.append('job_requisition_id', String(form.job_requisition_id))
    }
    if (form.area) {
      form.area.forEach((area) => {
        formData.append('area[]', area)
      })
    }
    formData.append('why_you_want_to_work_here', form.why_you_want_to_work_here)
    formData.append('reasons_to_hire_you', form.reasons_to_hire_you)
    if (form.resume) {
      formData.append('resume', form.resume)
    }
    if (form.salary_range_id) {
      formData.append('salary_range_id', form.salary_range_id)
    }
    if (form.portfolio_link) {
      formData.append('portfolio_link', form.portfolio_link)
    }
    return apiService.post(`/job-application/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async downloadPdf(id: number): Promise<void> {
    return downloadBlobFromApi(`/job-application/${id}/download-pdf`, `candidatura_${id}.pdf`)
  }

  async sendToActiveCampaign(id: number): Promise<{ message: string; isSentToClint: boolean }> {
    return apiService.post(`/job-application/send-to-active-campaign/${id}`).then((response) => response.data)
  }
}
